<template>
    <v-container fluid class="pa-4 overtime-page" style="height: 85dvh; overflow-y: auto">
        <!-- Browse state -->
        <div v-if="!overtime_state">
            <div class="d-flex align-center justify-space-between mb-4 flex-wrap" style="gap: 12px">
                <div>
                    <span class="text-h5 font-weight-bold d-flex align-center">
                        <v-icon left color="primary">mdi-timer-outline</v-icon>
                        Overtime Requests
                    </span>
                    <p class="text-body-2 grey--text mb-0 mt-1">Request and track your overtime</p>
                </div>
                <v-btn color="primary" class="text-none" @click="requestOvertime">
                    <v-icon left small>mdi-plus</v-icon>
                    Request Overtime
                </v-btn>
            </div>

            <span class="text-caption font-weight-bold grey--text text--darken-1">PENDING</span>
            <v-row class="mt-1 mb-2">
                <template v-if="isLoadingOvertime">
                    <v-col v-for="n in 3" :key="n" cols="12" sm="6" md="4">
                        <v-skeleton-loader type="card" />
                    </v-col>
                </template>
                <v-col cols="12" v-else-if="pendingRequests.length === 0">
                    <div class="empty-state">
                        <v-icon color="grey lighten-1" size="40">mdi-timer-sand-empty</v-icon>
                        <p class="text-body-2 grey--text mb-0 mt-2">No pending overtime requests</p>
                    </div>
                </v-col>
                <v-col v-else v-for="item in pendingRequests" :key="item.id" cols="12" sm="6" md="4">
                    <v-card outlined class="pa-3 overtime-card">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <v-chip x-small label :color="getOvertimeStatusColor(item.overtime_status?.name)" text-color="white">
                                <v-icon x-small left>{{ getOvertimeStatusIcon(item.overtime_status?.name) }}</v-icon>
                                {{ item.overtime_status?.name || "Pending" }}
                            </v-chip>
                            <v-btn icon x-small color="error" @click.stop="deleteOvertimeRequest(item)">
                                <v-icon small>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                            <v-chip small label>
                                <v-icon x-small left>mdi-calendar</v-icon>
                                {{ $date(item.planned_data).format("MMM D, YYYY") }}
                            </v-chip>
                            <v-chip small label>
                                <v-icon x-small left>mdi-clock-outline</v-icon>
                                {{ item.overtime }}
                            </v-chip>
                        </div>
                        <div class="text-caption grey--text mb-2">
                            Approver: {{ (item.overtime_approver?.first_name ? `${item.overtime_approver.first_name} ${item.overtime_approver.last_name || ''}`.trim() : item.overtime_approver?.name) || "No approver" }}
                        </div>
                        <v-divider class="mb-2" />
                        <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
                            {{ item.overtime_data.length }} task{{ item.overtime_data.length !== 1 ? "s" : "" }}
                        </div>
                        <div v-for="(task, taskIndex) in item.overtime_data" :key="taskIndex" class="task-line mb-1">
                            <span class="font-weight-medium text-body-2">{{ task.task?.name || "Task has no name" }}</span>
                            <div v-if="task.note_per_task" class="text-caption grey--text font-italic">{{ task.note_per_task }}</div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <span class="text-caption font-weight-bold grey--text text--darken-1">HISTORY</span>
            <v-row class="mt-1">
                <template v-if="isLoadingOvertime">
                    <v-col v-for="n in 3" :key="'h' + n" cols="12" sm="6" md="4">
                        <v-skeleton-loader type="card" />
                    </v-col>
                </template>
                <v-col cols="12" v-else-if="historyRequests.length === 0">
                    <div class="empty-state">
                        <v-icon color="grey lighten-1" size="40">mdi-history</v-icon>
                        <p class="text-body-2 grey--text mb-0 mt-2">No past requests yet</p>
                    </div>
                </v-col>
                <v-col v-else v-for="item in historyRequests" :key="item.id" cols="12" sm="6" md="4">
                    <v-card outlined class="pa-3 overtime-card">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <v-chip x-small label :color="getOvertimeStatusColor(item.overtime_status?.name)" text-color="white">
                                <v-icon x-small left>{{ getOvertimeStatusIcon(item.overtime_status?.name) }}</v-icon>
                                {{ item.overtime_status?.name }}
                            </v-chip>
                        </div>
                        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                            <v-chip small label>
                                <v-icon x-small left>mdi-calendar</v-icon>
                                {{ $date(item.planned_data).format("MMM D, YYYY") }}
                            </v-chip>
                            <v-chip small label>
                                <v-icon x-small left>mdi-clock-outline</v-icon>
                                {{ item.overtime }}
                            </v-chip>
                        </div>
                        <div class="text-caption grey--text mb-2">
                            Approver: {{ (item.overtime_approver?.first_name ? `${item.overtime_approver.first_name} ${item.overtime_approver.last_name || ''}`.trim() : item.overtime_approver?.name) || "No approver" }}
                        </div>
                        <div v-if="item.approver_note" class="text-caption grey--text font-italic mb-2">
                            Note from {{ (item.overtime_approver?.first_name ? `${item.overtime_approver.first_name} ${item.overtime_approver.last_name || ''}`.trim() : item.overtime_approver?.name) || "your approver" }}: {{ item.approver_note }}
                        </div>
                        <v-divider class="mb-2" />
                        <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
                            {{ item.overtime_data.length }} task{{ item.overtime_data.length !== 1 ? "s" : "" }}
                        </div>
                        <div v-for="(task, taskIndex) in item.overtime_data" :key="taskIndex" class="task-line mb-1">
                            <span class="font-weight-medium text-body-2">{{ task.task?.name || "Task has no name" }}</span>
                            <div v-if="task.note_per_task" class="text-caption grey--text font-italic">{{ task.note_per_task }}</div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <!-- Request creation state -->
        <v-card v-else outlined>
            <v-card-title class="py-3 px-4">
                <span class="text-h6 font-weight-bold d-flex align-center">
                    <v-icon left color="primary">mdi-timer-outline</v-icon>
                    Request Overtime
                </span>
                <v-spacer />
                <v-btn outlined small class="text-none" @click="cancelRequestOvertime">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-row>
                    <v-col cols="12" md="4">
                        <v-list dense flat outlined style="height: 60dvh; overflow-y: auto">
                            <v-subheader>Please select the task(s) you would like to request overtime for</v-subheader>
                            <div class="px-2">
                                <v-text-field placeholder="search here..." outlined dense hide-details
                                    prepend-inner-icon="mdi-magnify" v-model="searchTask"></v-text-field>
                            </div>
                            <div class="px-2 mt-2">
                                <v-text-field v-model="quickTaskName" placeholder="Type a task name and press Enter..."
                                    outlined dense hide-details :loading="isCreatingQuickTask"
                                    :disabled="isCreatingQuickTask" @keydown.enter="createQuickTask">
                                    <template v-slot:prepend-inner>
                                        <v-icon small color="grey">mdi-plus</v-icon>
                                    </template>
                                </v-text-field>
                            </div>
                            <v-list-item-group color="primary">
                                <v-list-item v-for="(item, i) in filteredTasks" :key="i">
                                    <v-list-item-icon>
                                        <v-icon>mdi-notebook-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                                        <v-list-item-subtitle class="text-caption">{{ item.company?.name || "no company" }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-btn icon small @click.stop="addItem(item)">
                                            <v-icon>mdi-plus</v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-col>
                    <v-col cols="12" md="8" style="height: 60dvh; overflow-y: auto;">
                        <v-row v-if="overtime_data.length > 0" style="display:flex;align-items:end;">
                            <v-col cols="12" md="4">
                                <v-autocomplete :items="approvers" color="white" item-text="approver_label" item-value="id"
                                    label="Approver" outlined dense hide-details v-model="approver"></v-autocomplete>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-menu ref="menu" v-model="menu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="auto">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="date" label="Planned date"
                                            prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"
                                            outlined dense hide-details></v-text-field>
                                    </template>
                                    <v-date-picker v-model="date" no-title scrollable @input="menu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" md="4">
                                <label class="text-caption font-weight-bold grey--text text--darken-1 d-block mb-1">
                                    TIME TO TAKE
                                </label>
                                <vue-timepicker v-model="allocated_time" format="HH:mm" :minute-interval="5"
                                    input-width="100%" close-on-complete></vue-timepicker>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea outlined dense rows="2" hide-details label="Note (optional)"
                                    v-model="note"></v-textarea>
                            </v-col>
                        </v-row>
                        <v-subheader v-if="overtime_data.length > 0" class="pl-0">SELECTED TASKS</v-subheader>
                        <v-card outlined v-for="(item, itemIndex) in overtime_data" :key="itemIndex" class="mb-2">
                            <v-card-title class="py-2">
                                <v-btn icon x-small @click.stop="removeItem(item)">
                                    <v-icon small>mdi-minus</v-icon>
                                </v-btn>
                                <span class="text-body-2 font-weight-medium ml-1">{{ item.name }}</span>
                                <v-spacer />
                                <v-chip x-small dark label :color="item.priority?.color">
                                    {{ item.priority?.name || "no priority" }}
                                </v-chip>
                            </v-card-title>
                            <v-card-text class="pt-0">
                                <v-textarea outlined dense rows="2" hide-details placeholder="Notes for this task"
                                    v-model="item.note_per_task"></v-textarea>
                            </v-card-text>
                        </v-card>
                        <v-card-actions v-if="overtime_data.length > 0" class="px-0">
                            <v-spacer />
                            <v-btn text class="text-none" @click.stop="cancelRequest">Cancel</v-btn>
                            <v-btn depressed :color="isOvertimeFormValid ? 'primary' : 'grey'"
                                :disabled="!isOvertimeFormValid || isSubmitting" :loading="isSubmitting"
                                class="pl-8 pr-8 text-none white--text" @click.stop="requestAnOvertime">
                                Request
                            </v-btn>
                        </v-card-actions>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Delete confirmation -->
        <v-dialog v-model="deleteWarningState" max-width="420">
            <v-card>
                <v-card-title class="text-h6">Are you sure about this action?</v-card-title>
                <v-card-text>You are about to delete this overtime request. This cannot be undone.</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="deleteWarningState = false">Cancel</v-btn>
                    <v-btn color="red" dark @click="proceedDelete">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
/**vuex */
import { mapGetters } from "vuex";
/**shared helpers */
import { getOvertimeStatusColor, getOvertimeStatusIcon, getCompanyManagerApprovers } from "./leaveOvertimeUtils";

export default {
    inject: ["users", "roleManagers", "personalTasks", "assignedTasks", "addPersonalTask"],
    data: () => {
        return {
            overtime: [],
            statuses: [],
            isLoadingOvertime: true,
            overtime_state: false,
            overtime_data: [],
            searchTask: null,
            quickTaskName: "",
            isCreatingQuickTask: false,
            date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
            menu: false,
            allocated_time: "",
            note: "",
            approver: null,
            deleteWarningState: false,
            overtimeToDelete: null,
            isSubmitting: false,
        };
    },
    async mounted() {
        await Promise.all([this.getOvertime(), this.getOvertimeStatuses()]);
    },
    computed: {
        ...mapGetters({
            user: "auth/get_user",
            get_roles: "auth/get_roles",
        }),
        // Combine personal and assigned tasks
        tasks() {
            const personal = this.personalTasks ? this.personalTasks() : [];
            const assigned = this.assignedTasks ? this.assignedTasks() : [];

            // Combine and deduplicate by task ID
            const combined = [...personal, ...assigned];
            return combined.filter((task, index, self) => index === self.findIndex((t) => t.id === task.id));
        },
        filteredTasks() {
            if (this.searchTask !== null && this.searchTask !== "") {
                return this.tasks.filter((q) => q.name.toLowerCase().includes(this.searchTask.toLowerCase()));
            }
            return this.tasks;
        },
        approvers() {
            return getCompanyManagerApprovers(this.users(), this.user, this.roleManagers ? this.roleManagers() : [], this.get_roles);
        },
        pendingStatusId() {
            const found = this.statuses.find((s) => (s.name || "").toLowerCase().includes("pending"));
            return found ? found.id : 1;
        },
        pendingRequests() {
            return this.overtime
                .filter((q) => q.overtime_status?.id === this.pendingStatusId)
                .sort((a, b) => b.id - a.id);
        },
        historyRequests() {
            return this.overtime
                .filter((q) => q.overtime_status?.id !== this.pendingStatusId)
                .sort((a, b) => b.id - a.id);
        },
        isOvertimeFormValid() {
            return !!(this.approver && this.allocated_time && this.date && this.overtime_data.length > 0);
        },
    },
    methods: {
        getOvertimeStatusColor,
        getOvertimeStatusIcon,
        deleteOvertimeRequest(data) {
            this.overtimeToDelete = data;
            this.deleteWarningState = true;
        },
        async proceedDelete() {
            try {
                await this.deleteOvertimeRequestAPI(this.overtimeToDelete.id);
                this.$toast.success("Overtime request deleted");
                this.deleteWarningState = false;
                this.overtimeToDelete = null;
            } catch (error) {
                console.log("error on delete overtime request - component level", error);
            }
        },
        requestOvertime() {
            this.overtime_state = true;
        },
        cancelRequestOvertime() {
            this.overtime_state = false;
        },
        async createQuickTask() {
            const name = this.quickTaskName.trim();
            if (!name || this.isCreatingQuickTask) {
                return;
            }

            this.isCreatingQuickTask = true;
            try {
                const { data } = await this.$axios.post("api/tasks/create_task_v2", {
                    tasks: [{
                        name,
                        description: null,
                        priority_id: null,
                        status_id: 1,
                        due_date: null,
                        company_id: null,
                        project_id: null,
                        assignee_ids: [this.user.id],
                        allocated_time: null,
                        ...(this.user?.role_id && { role_id: this.user.role_id, user_role_id: this.user.role_id }),
                    }],
                });

                if (!data.response) {
                    throw new Error(data.message || "Failed to create task");
                }

                const realTask = data.data?.[0];
                if (realTask) {
                    this.addPersonalTask(realTask);
                    this.addItem(realTask);
                }

                this.quickTaskName = "";
            } catch (error) {
                console.log("error on creating quick task - component level", error);
                this.$toast.error("Error creating task");
            } finally {
                this.isCreatingQuickTask = false;
            }
        },
        addItem(data) {
            const exists = this.overtime_data.some((item) => item.id === data.id);

            if (!exists) {
                this.overtime_data.push({ ...data, note_per_task: "" });
            } else {
                this.$toast.error("Task already added");
            }
        },
        removeItem(data) {
            this.overtime_data = this.overtime_data.filter((item) => item.id !== data.id);
        },
        cancelRequest() {
            this.overtime_data = [];
            this.approver = null;
            this.allocated_time = "";
            this.note = "";
            this.overtime_state = false;
            this.date = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
        },
        async requestAnOvertime() {
            if (!this.isOvertimeFormValid) {
                this.$toast.error("Please select an approver, time, and at least one task");
                return;
            }

            this.isSubmitting = true;
            const task_walk = this.overtime_data.map((q) => ({
                task_id: q.id,
                note_per_task: q.note_per_task,
            }));
            const tp = {
                user_id: this.user.id,
                approver_id: this.approver,
                overtime: this.allocated_time,
                planned_data: this.date,
                ...(this.note !== "" ? { note: this.note } : {}),
                tasks: task_walk,
            };
            try {
                await this.requestOvertimeAPI(tp);
                this.$toast.success("Overtime request submitted!");
                this.cancelRequest();
            } catch (error) {
                console.log("error on requesting overtime - component level", error);
                this.$toast.error("Error occurred!");
            } finally {
                this.isSubmitting = false;
            }
        },

        /**
        get overtime data
        */
        async getOvertime() {
            this.isLoadingOvertime = true;
            try {
                const { data } = await this.$axios.get(`api/overtime/${this.user.id}`);
                this.overtime = data;
            } catch (error) {
                console.log("error on fetching overtime data - service layer", error);
            } finally {
                this.isLoadingOvertime = false;
            }
        },

        /**
        get overtime statuses
        */
        async getOvertimeStatuses() {
            try {
                const { data } = await this.$axios.get("api/overtime/status");
                this.statuses = data;
            } catch (error) {
                console.log("error on fetching overtime status - service layer", error);
            }
        },

        /**
        request an overtime
        @params
        item? object
        */
        async requestOvertimeAPI(item) {
            try {
                await this.$axios.post("api/overtime", item);
                await this.getOvertime();
            } catch (error) {
                console.log("error on requesting overtime - service layer", error);
                throw error;
            }
        },

        /**
        delete data with user permission
        @payload
        int?: record_id
        */
        async deleteOvertimeRequestAPI(record_id) {
            try {
                await this.$axios.delete(`api/overtime/${record_id}`);
                await this.getOvertime();
            } catch (error) {
                console.log("error on delete overtime request - service layer", error);
            }
        },
    },
};
</script>

<style scoped>
.overtime-card {
    height: 100%;
}

.task-line:last-child {
    margin-bottom: 0 !important;
}

.empty-state {
    text-align: center;
    padding: 32px 16px;
}

.overtime-page ::v-deep .vue__time-picker {
    width: 100%;
}

.overtime-page ::v-deep .vue__time-picker input.display-time {
    width: 100%;
    height: 40px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    padding: 0 12px;
    font-size: 0.875rem;
    box-sizing: border-box;
}
</style>
